import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Filter,
  downloadCSV,
  handlePODetailsClick,
  CalculateTableArray,
} from '../../utils/Utils'
import { useTranslation } from 'react-i18next'
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getFilteredRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getPaginationRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues
} from '@tanstack/react-table'

import i18next from 'i18next'
import Loading from '../../components/Loading'
import PopupModal from '../../components/PopupModal'
import { IndeterminateCheckbox } from './NewPoList'
import PopupBanner from '../../components/PopupBanner'
import axiosInstance from '../../api/axiosInstance'
import Loader from '../../components/Loader'

const SORT_ICONS = {
  asc: { viewBox: '0 0 10 14', d: 'M5 13V1m0 0L1 5m4-4 4 4' },
  desc: { viewBox: '0 0 10 14', d: 'M5 1v12m0 0 4-4m-4 4L1 9' },
  none: { viewBox: '0 0 16 20', d: 'M4 6v13m0 0 3-3m-3 3-3-3m11-2V1m0 0L9 4m3-3 3 3' }
}

const SortIcon = ({ direction }) => {
  const { viewBox, d } = SORT_ICONS[direction || 'none']
  return (
    <svg
      className='w-3 h-4 text-gray-500'
      aria-hidden='true'
      xmlns='http://www.w3.org/2000/svg'
      fill='none'
      viewBox={viewBox}
    >
      <path stroke='currentColor' strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d={d} />
    </svg>
  )
}

const ChangeRequestTable = ({
  tableId,
  tableData,
  columns,
  filterData,
  fetchAllRequests,
  showChangeRequestBanner,
  setShowChangeRequestBanner,
  poRow,
  crIdResponse
}) => {
  const { t } = useTranslation()
  const user = JSON.parse(localStorage.getItem('userData'))

  const data = useMemo(() => tableData ?? [], [tableData])

  const [sorting, setSorting] = useState([])
  const [selectedArray, setSelectedArray] = useState([])
  const [showCancelOrderModal, setShowCancelOrderModal] = useState(false)
  const [globalFilter, setGlobalFilter] = useState('')
  const [columnFilters, setColumnFilters] = useState([])
  const [values, setValues] = useState([])
  const [rowSelection, setRowSelection] = useState({})
  const [columnResizeMode, setColumnResizeMode] = useState('onChange')
  const [columnResizeDirection, setColumnResizeDirection] = useState('ltr')
  const [ClearState, setClearState] = useState(false)
  const [loading, setLoading] = useState(false)

  const [showSidePanel, setShowSidePanel] = useState(false)
  const [poskuData, setposkuData] = useState('')
  const [showInvalidDataError, setShowDataError] = useState(false)
  const [invalidResults, setInvalidResults] = useState([])
  const [errorMsg, setErrorMsg] = useState(null)

  const { Page, Gotopage, Show, ClearFilters, PleaseSelectaPO } = t('BasicTable')
  const {
    GenerateConfirm,
    SelectPOSKUButton,
    SelectPOSKUsDownload,
    Subtext,
    Alert,
    POSkuList,
    Confirm
  } = t('HFTBuyerConfirmRegenerationPage')


  const table = useReactTable({
    data,
    columns,
    columnResizeMode,
    columnResizeDirection,
    state: {
      rowSelection,
      sorting,
      columnFilters,
      globalFilter
    },
    defaultColumn: { size: 50 },
    onRowSelectionChange: setRowSelection,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    getPaginationRowModel: getPaginationRowModel()
  })

  const requiredOutput = () => {
    const selectedRowRaw = table?.getSelectedRowModel().flatRows
    const requiredData = selectedRowRaw?.map((row) => row?.original) ?? []
    setSelectedArray(requiredData)
    return requiredData
  }

  useEffect(() => {
    requiredOutput()
  }, [rowSelection])

  const confirmpoSku = (e) => {
    handleRecreateConfirm(e, poskuData)
  }
  const cleared = () => {
    setColumnFilters([])
    setClearState((prevState) => !prevState)
  }

  const generateCSVData = (table) =>
    table?.getFilteredRowModel()?.rows?.map((row) => row?.original)

  const handlePageChange = (pageIndex) => {
    table.setPageIndex(pageIndex)
  }

  const handlePageSizeChange = (e) => {
    table.setPageSize(Number(e.target.value))
  }

  const handleInputChange = (e) => {
    const page = e.target.value ? Number(e.target.value) - 1 : 0
    handlePageChange(page)
  }

  const handleColumnResizeEnd = (header, tableId) => {
    const newWidth = header.getSize()
    const storedWidths = JSON.parse(localStorage.getItem('column-widths')) || {}
    if (!storedWidths[tableId]) {
      storedWidths[tableId] = {}
    }
    storedWidths[tableId][header.id] = newWidth
    localStorage.setItem('column-widths', JSON.stringify(storedWidths))
  }

  const loadColumnWidths = (columns, tableId) => {
    const storedWidths = JSON.parse(localStorage.getItem('column-widths')) || {}
    if (storedWidths[tableId]) {
      columns.forEach((column) => {
        if (storedWidths[tableId][column.id]) {
          column.columnDef.size = Number(storedWidths[tableId][column.id])
        }
      })
    }
  }

  useEffect(() => {
    loadColumnWidths(table.getAllColumns(), tableId)
  }, [table, tableId])

  useEffect(() => {
    // remove stored column widths when the window is reloaded
    const handleBeforeUnload = () => {
      localStorage.removeItem('column-widths')
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  const openCancelOrderModal = () => {
    setShowCancelOrderModal(true)
  }

  const openPOSKUModal = () => {
    setShowSidePanel(true)
  }

  const closeCancelOrderModal = () => {
    setShowCancelOrderModal(false)
    setSelectedArray([])
    table?.resetRowSelection()
  }

  const buildPayload = (item) => ({
    id_po_supplier_portal: item?.IDPOSUPPLIERPORTAL,
    po_number: item?.PONUMBER,
    po_line_number: item?.POLINENUMBER,
    revision_number: item?.REASON_CODE_ID,
    sku: item?.SKU,
    upc: item?.UPC,
    vendor_number: item?.['VENDOR#'],
    vendor_name: item?.VENDORNAME,
    request_type: "Confirm",
    description: item?.DESCRIPTION,
    original_dc: item?.ORIGINALDC,
    original_ship_week: item?.ORIGINALSHIPWEEK,
    requested_ship_week: item?.REQUESTEDSHIPWEEK,
    current_ship_week: item?.CURRENTSHIPWEEK,
    hft_user_id: Number(user?.hftOracleId),
    supplier_contact_id: item?.SUPPLIERCONTACTID,
    supplier_response: "Awaiting Response",
    status: "OPEN",
    followups: item?.FOLLOWUPS,
    original_qty: item?.ORIGINALQTY,
    requested_qty: item?.REQUESTEDQTY,
    qtyOpen: item?.QTY_OPEN,
    inventory_analayst: item?.INEVNTORY_ANALYST,
    vendor_manager: item?.VENDOR_MANAGER,
    created_by: user?.fullName,
    ontimetarget: item?.ONTIMETARGET,
    shipweekdelay: item?.SHIPWEEKDELAY,
    unitCost: item?.UNITPRICE,
    extendedCost: item?.EXTENDEDPRICE,
  });

  const handleRecreateConfirm = (e, posku) => {
    e?.preventDefault();
    setLoading(true);

    let rows = selectedArray;

    if (posku) {
      const poSkuSet = new Set(posku.split(",").map((s) => s.trim()).filter(Boolean));
      rows = data.filter((item) => poSkuSet.has(item.PO_SKU));
    }

    const req_payload = (rows ?? []).map(buildPayload);

    axiosInstance
      .post(`/api/confirm-rejected/recreate-confirm`, req_payload)
      .then(() => {
        setShowChangeRequestBanner(true);
        fetchAllRequests();
        handleCloseSidePanel();
      })
      .catch((error) => {
        console.error(error); 
      })
      .finally(() => setLoading(false));

    closeCancelOrderModal();
  };

 

  const closePopUpBanner = () => {
    setShowChangeRequestBanner(false)
  }

  const handlePOSkuChange = (e) => {
    const value = e.target.value;
    setposkuData(value);
    validatePOSkuFormat(value);
  };

  const validatePOSkuFormat = (value) => {
    if (!value.trim()) {
      setErrorMsg('');
      return;
    }

    const items = value.split(',').map(item => item.trim());

    const isValid = items.every(item =>
      /^\d+-\d+$/.test(item)
    );

    if (!isValid) {
      setErrorMsg(
        'Please enter PO-SKU in the format PO-SKU, separated by commas. Example: 7143591-10234, 7143592-19876'
      );
    } else {
      setErrorMsg('');
    }
  };

  const handleCloseSidePanel = () => {
    setShowSidePanel(false);
    setposkuData('');
    setErrorMsg('');
  };

  return (
    <>
      {loading ?
        <Loading />
         :

        <div className='w-full flex justify-start items-start text-xs flex-col gap-4 max-h-screen'>
          <div className='w-full justify-between flex items-center text-xs'>
            <div className='relative flex justify-start items-center gap-3'>
              <div className='relative flex items-center gap-2'>
                <button
                  className={`px-4 h-[30px] rounded-md outline-none min-w-max text-white
              ${selectedArray?.length > 0 ? 'bg-[#415da1] cursor-pointer' : 'bg-gray-400 cursor-not-allowed'}`}
                  disabled={selectedArray?.length === 0}
                  onClick={openCancelOrderModal}
                >
                  {GenerateConfirm}
                </button>

                <button
                  className='px-4 h-[30px] rounded-md outline-none min-w-max text-white bg-[#415da1] cursor-pointer'
                  onClick={openPOSKUModal}
                >
                  {SelectPOSKUButton}
                </button>
              </div>
              <span> </span>


              <PopupModal
                isOpen={showCancelOrderModal}
                message={i18next.t('PopUpMessage.Doyouwanttoregenerateconfirmation')}
                onCancel={closeCancelOrderModal}
                cancelText={i18next.t('AcceptModal.Cancel')}
                onConfirm={handleRecreateConfirm}
                confirmText={i18next.t('AcceptModal.Confirm')}
              />
              <PopupBanner
                isOpen={showChangeRequestBanner}
                message={i18next.t('PopUpMessage.RegenerateConfirmSuccessfully')}
                onCancel={closePopUpBanner}
                text={poRow}
                text1={crIdResponse}
              />
            </div>

            <div className='flex items-center gap-2'>
              {columnFilters.length > 0 && (
                <div>
                  <button
                    className='font-medium text-white outline-none bg-[#415da1] w-[90px] rounded-md h-[30px]'
                    onClick={cleared}
                  >
                    {ClearFilters}
                  </button>
                </div>
              )}{' '}
              {data?.length !== 0 && downloadCSV('PO_list', generateCSVData(table))}
              <button
                className='border rounded p-1 font-medium text-sm'
                onClick={() => handlePageChange(0)}
                disabled={!table.getCanPreviousPage()}
              >
                {'<<'}
              </button>
              <button
                className='border rounded p-1 font-medium text-sm'
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                {'<'}
              </button>
              <button
                className='border rounded p-1 font-medium text-sm'
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                {'>'}
              </button>
              <button
                className='border rounded p-1 font-medium text-sm'
                onClick={() => handlePageChange(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              >
                {'>>'}
              </button>
              <span className='font-medium min-w-max flex items-center gap-1'>
                <div>{Page}</div>
                <strong>
                  {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                </strong>
              </span>
              <span className='min-w-max font-medium flex items-center gap-1'>
                | {Gotopage}
                <input
                  type='number'
                  id='pagination_input'
                  name='pagination_input'
                  value={table.getState().pagination.pageIndex + 1}
                  onChange={handleInputChange}
                  className='border p-1 rounded w-16'
                />
              </span>
              <select value={table.getState().pagination.pageSize} onChange={handlePageSizeChange}>
                {CalculateTableArray(table?.getFilteredRowModel()?.rows?.length).map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {Show} {pageSize}
                  </option>
                ))}
              </select>{' '}
            </div>
          </div>

          <div
            className='relative w-full overflow-auto scrollbar-gutter-stable max-h-[40vh] sm:max-h-[45vh] md:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[55vh]'
            style={{ scrollbarGutter: 'stable' }}
          >
            <div className='min-w-full sm:min-w-[640px] md:min-w-[768px] lg:min-w-[1024px]'>
              <table className='text-xs border border-gray-300 relative w-full'>
                <thead className='sticky top-0 z-10'>
                  {table?.getHeaderGroups()?.map((headerGroup, index) => (
                    <tr
                      key={headerGroup?.id + index}
                      className='divide-x-[1px] bg-[#e0ecfc] divide-gray-300 border-[#ccd9e4] border'
                    >
                      <th className='font-medium p-2 frozen-column' style={{ width: '50px' }}>
                        <IndeterminateCheckbox
                          checked={table?.getIsAllPageRowsSelected()}
                          indeterminate={table?.getIsSomePageRowsSelected()}
                          onChange={table?.getToggleAllPageRowsSelectedHandler()}
                        />
                      </th>
                      {headerGroup?.headers?.map((header) => (
                        <th
                          key={header?.id}
                          className='font-medium p-2 sticky top-0'
                          colSpan={header.colSpan}
                          style={{ width: header.getSize() }}
                        >
                          {header.isPlaceholder ? null : (
                            <div
                              className={`w-full flex flex-col items-center justify-center gap-2 ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''
                                }`}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              <div className='flex justify-center h-10 text-center'>
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                &nbsp;&nbsp;
                                <div>
                                  <SortIcon direction={header.column.getIsSorted()} />
                                </div>
                              </div>
                            </div>
                          )}
                          {header.column.getCanFilter() ? (
                            <div>
                              <Filter
                                tableId={tableId}
                                column={header.column}
                                table={table}
                                columnFilters={columnFilters}
                                values={values}
                                setValues={setValues}
                                header={header}
                                ClearState={ClearState}
                                filterValue={filterData}
                                hftid={user?.hftOracleId}
                              />
                            </div>
                          ) : null}
                          <div
                            onDoubleClick={() => header.column.resetSize()}
                            onMouseDown={(e) => {
                              header.getResizeHandler()(e)
                              document.addEventListener('mouseup', () => handleColumnResizeEnd(header, tableId), { once: true })
                            }}
                            onTouchStart={(e) => {
                              header.getResizeHandler()(e)
                              document.addEventListener('touchend', () => handleColumnResizeEnd(header, tableId), { once: true })
                            }}
                            className={`resizer ${table.options.columnResizeDirection} ${header.column.getIsResizing() ? 'isResizing' : ''
                              }`}
                            style={{
                              transform:
                                columnResizeMode === 'onEnd' && header.column.getIsResizing()
                                  ? `translateX(${(table.options.columnResizeDirection === 'rtl' ? -1 : 1) *
                                  (table.getState().columnSizingInfo.deltaOffset ?? 0)
                                  }px)`
                                  : ''
                            }}
                          />
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>

                <tbody>
                  {table?.getRowModel().rows?.map((row, index) => {
                    const rowClassName = index % 2 !== 0 ? 'bg-[#ecedf2]' : 'bg-[#ffffff]'
                    return (
                      <tr
                        key={row?.id + index}
                        className={`${rowClassName} text-center divide-x-[1px] divide-gray-300 border border-gray-300`}
                      >
                        <td className='px-2 py-2 frozen-column'>
                          <div className='w-full flex justify-center gap-2 min-w-min'>
                            <IndeterminateCheckbox
                              checked={row.getIsSelected()}
                              indeterminate={row.getIsSomeSelected()}
                              onChange={row.getToggleSelectedHandler()}
                              row={row.original}
                            />
                          </div>
                        </td>
                        {row?.getVisibleCells()?.map((cell) => (
                          <td
                            key={cell.id}
                            className='px-2 py-2'
                            style={{ width: cell.column.getSize() }}
                          >
                            {flexRender(cell?.column?.columnDef?.cell, cell?.getContext())}
                          </td>
                        ))}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {showSidePanel && (
            <>
              {/* Overlay */}
              <div
                className='fixed inset-0 bg-black bg-opacity-30 z-40 cursor-pointer'
                onClick={handleCloseSidePanel}
              />

              {/* Side Panel */}
              <aside
                className='fixed top-0 right-0 w-[600px] h-full bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-y-auto transition-transform duration-300 ease-in-out font-sans'
                style={{ top: 51, height: `calc(100vh - ${51 + 24}px)` }}
              >
                <button
                  className='absolute top-4 right-4 text-gray-500 hover:text-red-600 text-xl font-bold z-50'
                  onClick={handleCloseSidePanel}
                  aria-label='Close'
                >
                  ✕
                </button>

                <main className='max-w-4xl mx-auto sm:p-8'>
                  <header className='mb-8'>
                    <h1 className='text-2xl font-semibold text-slate-900'>{SelectPOSKUsDownload}</h1>
                    <p className='mt-3 text-slate-600'>{Subtext}</p>
                  </header>

                  <section className='space-y-4'>
                    {/* Textarea */}
                    <div>
                      <label className='block mb-1 text-md font-medium text-slate-700'>
                        {POSkuList}
                      </label>
                      <textarea
                        placeholder='Example: 7143591-10234, 7143592-19876, 7143593-2029'
                        rows={8}
                        value={poskuData}
                        className={`w-full rounded-md border p-3 text-sm focus:outline-none focus:ring-2 ${errorMsg
                          ? 'border-red-500 focus:ring-red-300'
                          : 'border-slate-300 focus:ring-[#0059A8]'
                          }`}
                        onChange={(e) => handlePOSkuChange(e)}
                      />
                      <p className='text-sm text-red-600 italic'>{Alert}</p>
                      {errorMsg && (
                        <div className='mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700'>
                          <p className='font-medium'>
                            {errorMsg}
                          </p>

                        </div>
                      )}
                    </div>

                    {/* Button after textarea */}
                    <button
                      className={`${(poskuData !== '' && !errorMsg) ? 'cursor-pointer bg-[#0059A8]' : 'cursor-not-allowed bg-[#4e88b8]'
                        } w-full h-12 rounded-md text-white font-medium transition flex items-center justify-center`}
                      disabled={poskuData === '' || errorMsg}
                      onClick={confirmpoSku}
                    >
                      {Confirm}
                    </button>
                  </section>
                </main>
              </aside>
            </>
          )}
        </div>
      }
    </>
  )
}

const ConfirmRegeneration = () => {
  const [requestData, setRequestData] = useState([])
  const [loading, setLoading] = useState(false)
  const [showChangeRequestBanner, setShowChangeRequestBanner] = useState(false)
  const [poRow, setrowPO] = useState('')
  const [crIdResponse, setCrIdResponse] = useState([])
  const [filter, setFilterData] = useState({})
  const navigate = useNavigate()
  const { t } = useTranslation()
  const tableId = 'BuyerReqTable'
  const { HFTBuyerConfirmRegeneration, Home } = t('HFTBuyerConfirmRegenerationPage')
  const user = JSON.parse(localStorage.getItem('userData'))

  useEffect(() => {
    fetchAllRequests()
  }, [])

  const headers = {
    CR: t('ConfirmPOpage.CR'),
    PONUMBER: t('ConfirmPOpage.PONUMBER'),
    SKU: t('ConfirmPOpage.SKU'),
    UPC: t('ConfirmPOpage.UPC'),
    DESCRIPTION: t('ConfirmPOpage.DESCRIPTION'),
    CHANGEDSW: t('ConfirmPOpage.CHANGEDSW'),
    CURRSW: t('POList.CURRENTSW'),
    DC: t('ConfirmPOpage.DC'),
    QUANTITY: t('ConfirmPOpage.QUANTITY'),
    CREATEDAT: t('ConfirmPOpage.CREATEDAT'),
    REQDC: t('ConfirmPOpage.REQDC'),
    STATUS: t('ConfirmPOpage.STATUS'),
    ORGSW: t('ConfirmPOpage.ORGSW'),
    INVENTORYANALYST: t('POList.INVENTORYANALYST'),
    UNITCOST: t('POList.UNITCOST'),
    EXTENDEDCOST: t('POList.EXTENDEDCOST')
  }


  const blankIfEmpty = (v) => (!v || String(v).trim() === '' ? 'BLANK' : v)
  const includesFilter = (row, id, value) => value.includes(row.getValue(id))
  const blankColumn = (header, key, extra = {}) => ({
    header,
    accessorFn: (row) => blankIfEmpty(row[key]),
    cell: ({ row }) => (row?.original?.[key] ? <span>{row.original[key]}</span> : null),
    filterFn: includesFilter,
    ...extra
  })

  const columns = useMemo(
    () => [

      {
        header: headers.PONUMBER,
        accessorKey: 'PONUMBER',
        cell: (info) => (
          <p
            className='underline cursor-pointer text-blue-500'
            onClick={() => handlePODetailsClick(user?.roleType, info?.getValue())}
          >
            {info?.getValue()}
          </p>
        ),
        filterFn: includesFilter
      },
      blankColumn(headers.SKU, 'SKU'),
      { header: headers.UPC, accessorKey: 'UPC', filterFn: includesFilter },
      blankColumn(headers.DESCRIPTION, 'DESCRIPTION', { size: 100 }),
      blankColumn(headers.CHANGEDSW, 'REQUESTEDSHIPWEEK'),
      blankColumn(headers.CURRSW, 'CURRENTSHIPWEEK'),
      blankColumn(headers.ORGSW, 'ORIGINALSHIPWEEK'),
      blankColumn(headers.DC, 'ORIGINALDC'),
      blankColumn(headers.REQDC, 'REQUESTEDDC'),
      blankColumn(headers.INVENTORYANALYST, 'INEVNTORY_ANALYST', { size: 80 }),
      { header: headers.UNITCOST, accessorKey: 'UNITPRICE' },
      { header: headers.EXTENDEDCOST, accessorKey: 'EXTENDEDPRICE' },
      { header: headers.QUANTITY, accessorKey: 'ORIGINALQTY' },

    ],
    [t]
  )

  const fetchAllRequests = () => {
    setLoading(true)
    let url = `/api/confirm-rejected/details`

    return axiosInstance
      .get(url)
      .then((response) => {
        setRequestData(response?.data)
        setLoading(false)
      })
      .catch((error) => {
        console.error(error)

      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <div className='w-full flex flex-col gap-6 justify-start items-start p-4'>
      <div className='w-full justify-between flex items-center'>
        <div className=''>
          <p className='text-xl'>{HFTBuyerConfirmRegeneration}</p>
        </div>
      </div>
      <div className='w-full text-xs flex justify-between items-center gap-1 font-medium text-gray-600'>
        <div className='flex'>
          <p className='flex gap-1 items-center justify-center'>
            <span className='hover:underline cursor-pointer' onClick={() => navigate('/buyer')}>
              {Home}
            </span>
            <span>{'>'}</span>
          </p>
          <p className='flex gap-1 items-center justify-center'>
            <span> {HFTBuyerConfirmRegeneration}</span>
          </p>
        </div>
      </div>
      {loading ? (
        <div className='fixed inset-0 z-50 flex items-center justify-center '>
          <div className='sm:w-[400px] w-[280px] flex flex-col gap-6'>
            <Loading type='Loading...' />
          </div>
        </div>
      ) : (
        <div className='w-full overflow-x-auto'>
          <ChangeRequestTable
            tableId={tableId}
            tableData={requestData}
            columns={columns}
            setFilterData={setFilterData}
            filterData={filter}
            fetchAllRequests={fetchAllRequests}
            showChangeRequestBanner={showChangeRequestBanner}
            setShowChangeRequestBanner={setShowChangeRequestBanner}
            poRow={poRow}
            crIdResponse={crIdResponse}
          />
        </div>
      )}{' '}
    </div>
  )
}

export default ConfirmRegeneration